'use client';

import { useState } from 'react';
import { Play } from 'lucide-react';

interface DemoVideoProps {
  videoUrl: string;
  title?: string;
}

/**
 * Extract YouTube video ID from various URL formats
 */
function getYouTubeId(url: string): string | null {
  if (!url) return null;

  // Match various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/,
    /^([a-zA-Z0-9_-]{11})$/, // Just the ID
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * DemoVideo Component
 *
 * Displays a YouTube video embed with a privacy-respecting lazy load.
 * Shows a thumbnail with play button first, loads iframe only on click.
 */
export default function DemoVideo({ videoUrl, title = 'Product Demo' }: DemoVideoProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoId = getYouTubeId(videoUrl);

  if (!videoId) {
    return null;
  }

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`;

  return (
    <div className="relative w-full aspect-video rounded-xl overflow-hidden shadow-lg bg-gray-900">
      {!isPlaying ? (
        <>
          {/* Thumbnail */}
          <img
            src={thumbnailUrl}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
            onLoad={(e) => {
              // YouTube serves a 120x90 grey placeholder with HTTP 200 — not a
              // 404 — for videos that have no maxresdefault. onError therefore
              // never fires and the poster renders as a stretched grey box, so
              // the fallback has to be driven off the loaded dimensions.
              const el = e.target as HTMLImageElement;
              if (el.naturalWidth <= 120 && !el.dataset.fallback) {
                el.dataset.fallback = '1';
                el.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
              }
            }}
            onError={(e) => {
              // Fall back to medium quality if maxres doesn't exist
              (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
            }}
          />

          {/* Overlay */}
          <div className="absolute inset-0 bg-black/30" />

          {/* Play button */}
          <button
            onClick={() => setIsPlaying(true)}
            className="absolute inset-0 flex items-center justify-center group"
            aria-label={`Play ${title}`}
          >
            <div className="w-20 h-20 rounded-full bg-red-600 flex items-center justify-center shadow-xl group-hover:bg-red-700 group-hover:scale-110 transition-all duration-200">
              <Play className="w-10 h-10 text-white ml-1" fill="white" />
            </div>
          </button>

          {/* YouTube branding hint */}
          <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/60 rounded text-white text-sm">
            Watch on YouTube
          </div>
        </>
      ) : (
        <iframe
          src={embedUrl}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      )}
    </div>
  );
}
